import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { leadService, quoteService } from '../services/api';
import {
  Inbox, Users, Loader2, ChevronRight, Plus, X, Search, RefreshCw, Lock,
  Send, Check, XCircle, Clock, Link as LinkIcon, ExternalLink, History,
} from 'lucide-react';

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  quoted: 'bg-purple-100 text-purple-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-gray-200 text-gray-600',
};
const SERVICE_TYPES = ['general', 'freight', 'white_glove', 'moving', 'driveaway', 'brokerage', 'courier', 'other'];
const fmtDate = (d) => { try { return new Date(d).toLocaleString(); } catch { return d || '—'; } };
const nameOf = (a) => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email;

const LeadManager = () => {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState(['new', 'contacted', 'quoted', 'won', 'lost']);
  const [assignees, setAssignees] = useState([]);
  const [filters, setFilters] = useState({ status: '', service_type: '', source: '', q: '', sort_by: 'created_at', sort_dir: 'desc' });
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([leadService.getSources(), leadService.getAssignees()]);
      setSources(s.sources || []); setStatuses(s.statuses || statuses); setAssignees(a.assignees || []);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [list, st] = await Promise.all([leadService.listLeads(params), leadService.getStats()]);
      setData(list); setStats(st); setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      else setError('Failed to load leads.');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  if (disabled) {
    return (
      <div className="max-w-3xl mx-auto text-center py-20" data-testid="leads-disabled-notice">
        <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Quote &amp; Lead Management is not active</h2>
        <p className="text-gray-600 mt-2">Enable the <code className="text-sm bg-gray-100 px-1 rounded">quote_lead_enabled</code> feature flag (Soft Go-Live) to manage leads.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="lead-manager">
      <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
        <Link to="/admin/dashboard" className="hover:text-gray-700" data-testid="leads-breadcrumb-home">Admin Panel</Link>
        <ChevronRight className="w-4 h-4 mx-1" /><span className="text-gray-900 font-medium">Leads</span>
      </nav>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="w-7 h-7 text-blue-600" /> Leads</h1>
          <p className="text-gray-600 mt-1">Manage inbound leads across every service line.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="leads-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => setShowNew(true)} data-testid="leads-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"><Plus className="w-4 h-4" /> New Lead</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="leads-stats">
          <div className="bg-white border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold text-gray-900" data-testid="leads-stat-total">{stats.total}</p></div>
          {statuses.map((s) => {
            const row = (stats.by_status || []).find((r) => r.key === s);
            return <div key={s} className="bg-white border border-gray-200 rounded-lg p-3"><p className="text-xs text-gray-500 capitalize">{s}</p><p className="text-2xl font-bold text-gray-900">{row ? row.count : 0}</p></div>;
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-3 items-end" data-testid="leads-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name / email / phone" data-testid="leads-search" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="leads-filter-status" className="py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
          <option value="">All statuses</option>{statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.service_type} onChange={(e) => setFilters((f) => ({ ...f, service_type: e.target.value }))} data-testid="leads-filter-service" className="py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
          <option value="">All services</option>{SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} data-testid="leads-filter-source" className="py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
          <option value="">All sources</option>{sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={filters.sort_by} onChange={(e) => setFilters((f) => ({ ...f, sort_by: e.target.value }))} data-testid="leads-sort-by" className="py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" aria-label="Sort by">
          <option value="created_at">Newest / Created</option>
          <option value="updated_at">Updated</option>
          <option value="status">Status</option>
          <option value="full_name">Name</option>
        </select>
        <button
          type="button"
          onClick={() => setFilters((f) => ({ ...f, sort_dir: f.sort_dir === 'asc' ? 'desc' : 'asc' }))}
          data-testid="leads-sort-dir"
          aria-label={`Sort direction: ${filters.sort_dir === 'asc' ? 'ascending' : 'descending'}`}
          className="py-2 px-3 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {filters.sort_dir === 'asc' ? '↑ Asc' : '↓ Desc'}
        </button>
      </div>

      {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16" data-testid="leads-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : data.items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center px-6" data-testid="leads-empty">
            <Inbox className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium">No leads yet</p>
            <p className="text-gray-500 text-sm mt-1">Inbound submissions and manual entries will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="leads-table">
              <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Assigned</th><th className="px-4 py-3">Created</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.items.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelected(l)} className="hover:bg-blue-50 cursor-pointer">
                    <td className="px-4 py-3"><p className="font-medium text-gray-900">{l.full_name}</p><p className="text-xs text-gray-500">{l.email}</p></td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{(l.service_type || '').replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-gray-600">{l.source_label}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[l.status] || 'bg-gray-100'}`}>{l.status}</span></td>
                    <td className="px-4 py-3 text-gray-600">{assignees.find((a) => a.id === l.assigned_to) ? nameOf(assignees.find((a) => a.id === l.assigned_to)) : '—'}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <LeadDetail lead={selected} statuses={statuses} assignees={assignees} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}
      {showNew && <NewLead onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const Overlay = ({ children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={testid} onClick={onClose}>
    <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

const LeadDetail = ({ lead, statuses, assignees, onClose, onSaved }) => {
  const [status, setStatus] = useState(lead.status);
  const [assignedTo, setAssignedTo] = useState(lead.assigned_to || '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const payload = {};
      if (status !== lead.status) payload.status = status;
      if ((assignedTo || null) !== (lead.assigned_to || null)) payload.assigned_to = assignedTo || null;
      if (note.trim()) payload.note = note.trim();
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await leadService.updateLead(lead.id, payload);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed.'); } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} testid="lead-detail-modal">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="font-semibold text-gray-900">Lead — {lead.full_name}</h2>
        <div className="flex items-center gap-3">
          <a href={`/admin/customer-360?customer=${lead.id}`} target="_blank" rel="noopener noreferrer" data-testid="lead-detail-open-360" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Open Customer 360</a>
          <button onClick={onClose} data-testid="lead-detail-close" aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-gray-500">Email</p><p className="text-gray-900 break-all">{lead.email}</p></div>
          <div><p className="text-gray-500">Phone</p><p className="text-gray-900">{lead.phone || '—'}</p></div>
          <div><p className="text-gray-500">Service</p><p className="text-gray-900 capitalize">{(lead.service_type || '').replace('_', ' ')}</p></div>
          <div><p className="text-gray-500">Source</p><p className="text-gray-900">{lead.source_label}</p></div>
          {lead.origin && <div><p className="text-gray-500">Origin</p><p className="text-gray-900">{lead.origin}</p></div>}
          {lead.destination && <div><p className="text-gray-500">Destination</p><p className="text-gray-900">{lead.destination}</p></div>}
        </div>
        {lead.message && <div className="text-sm"><p className="text-gray-500">Message</p><p className="text-gray-900">{lead.message}</p></div>}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ld-status">Status</label>
            <select id="ld-status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lead-detail-status" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ld-assignee">Assign to</label>
            <select id="ld-assignee" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} data-testid="lead-detail-assignee" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
              <option value="">Unassigned</option>
              {assignees.map((a) => <option key={a.id} value={a.id}>{nameOf(a)}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ld-note">Add note</label>
          <textarea id="ld-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="lead-detail-note" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" placeholder="Optional note" />
        </div>

        {(lead.notes || []).length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="lead-detail-notes">
            {lead.notes.map((n) => <div key={n.id} className="text-xs bg-gray-50 border border-gray-100 rounded p-2"><span className="text-gray-500">{n.author} · {fmtDate(n.created_at)}</span><p className="text-gray-800">{n.text}</p></div>)}
          </div>
        )}

        <QuotesPanel leadId={lead.id} serviceType={lead.service_type} />

        {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="lead-detail-save" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </Overlay>
  );
};

const NewLead = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', service_type: 'general', message: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { await leadService.createLead({ ...form, source: 'manual_entry' }); onCreated(); }
    catch (e2) { setErr(e2?.response?.data?.detail || 'Create failed.'); } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} testid="lead-new-modal">
      <form onSubmit={create}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">New Lead (manual entry)</h2>
          <button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nl-name">Full name *</label><input id="nl-name" required value={form.full_name} onChange={upd('full_name')} data-testid="lead-new-name" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nl-email">Email *</label><input id="nl-email" type="email" required value={form.email} onChange={upd('email')} data-testid="lead-new-email" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nl-phone">Phone</label><input id="nl-phone" value={form.phone} onChange={upd('phone')} data-testid="lead-new-phone" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nl-service">Service</label><select id="nl-service" value={form.service_type} onChange={upd('service_type')} data-testid="lead-new-service" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">{SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nl-msg">Message</label><textarea id="nl-msg" rows={2} value={form.message} onChange={upd('message')} data-testid="lead-new-message" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
          {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} data-testid="lead-new-submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
          </div>
        </div>
      </form>
    </Overlay>
  );
};

const QUOTE_STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-indigo-100 text-indigo-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-rose-100 text-rose-800',
  expired: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-gray-200 text-gray-600',
};

const QuoteRow = ({ q, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const portalLink = q.public_token ? `${window.location.origin}/quote/${q.public_token}` : null;
  const isTerminal = ['accepted', 'declined', 'expired', 'cancelled'].includes(q.status);

  const run = async (label, fn) => {
    setBusy(label); setErr(null);
    try { await fn(); onChanged(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      setErr(e?.response?.status === 403 ? 'Quote Workflow is not active (enable quote_workflow_enabled).' : (d || 'Action failed.'));
    } finally { setBusy(null); }
  };

  const send = () => run('send', () => quoteService.sendQuote(q.id));
  const mark = (status) => run(status, () => quoteService.setQuoteStatus(q.id, { status }));
  const copyLink = async () => {
    if (!portalLink) return;
    try { await navigator.clipboard.writeText(portalLink); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  return (
    <div data-testid={`quote-row-${q.id}`} className="bg-gray-50 border border-gray-100 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full font-medium capitalize ${QUOTE_STATUS_STYLES[q.status] || 'bg-gray-100'}`} data-testid={`quote-status-${q.id}`}>{q.status}</span>
          <span className="text-gray-600">{(q.line_items || []).length} item(s)</span>
        </span>
        <span className="font-semibold text-gray-900">{money(q.total)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {q.status === 'draft' && (
          <button type="button" onClick={send} disabled={busy} data-testid={`quote-send-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            {busy === 'send' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send Quote
          </button>
        )}
        {(q.status === 'sent' || q.status === 'viewed') && (
          <>
            <button type="button" onClick={() => mark('accepted')} disabled={busy} data-testid={`quote-accept-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"><Check className="w-3 h-3" /> Accepted</button>
            <button type="button" onClick={() => mark('declined')} disabled={busy} data-testid={`quote-decline-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"><XCircle className="w-3 h-3" /> Declined</button>
            <button type="button" onClick={() => mark('expired')} disabled={busy} data-testid={`quote-expire-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"><Clock className="w-3 h-3" /> Expired</button>
          </>
        )}
        {portalLink && (
          <>
            <a href={portalLink} target="_blank" rel="noopener noreferrer" data-testid={`quote-portal-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"><ExternalLink className="w-3 h-3" /> View Portal</a>
            <button type="button" onClick={copyLink} data-testid={`quote-copy-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"><LinkIcon className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy Link'}</button>
          </>
        )}
        {(q.status_history || []).length > 0 && (
          <button type="button" onClick={() => setShowHistory((s) => !s)} data-testid={`quote-history-toggle-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900"><History className="w-3 h-3" /> History</button>
        )}
        {isTerminal && !portalLink && <span className="text-xs text-gray-400 italic">No portal link</span>}
      </div>

      {showHistory && (
        <div className="space-y-1 pt-1" data-testid={`quote-history-${q.id}`}>
          {(q.status_history || []).map((h) => (
            <div key={h.id} className="text-[11px] text-gray-600 bg-white border border-gray-100 rounded px-2 py-1">
              <span className="capitalize font-medium text-gray-800">{h.from} → {h.to}</span>
              <span className="text-gray-400"> · {h.by} · {fmtDate(h.at)}</span>
              {h.note && <span className="text-gray-500"> — {h.note}</span>}
            </div>
          ))}
        </div>
      )}
      {err && <p role="alert" className="text-[11px] text-red-600">{err}</p>}
    </div>
  );
};

const QuotesPanel = ({ leadId, serviceType }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [taxRate, setTaxRate] = useState(0);
  const [validUntil, setValidUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await quoteService.listQuotes({ lead_id: leadId }); setQuotes(d.items || []); setDisabled(false); }
    catch (e) { if (e?.response?.status === 403) setDisabled(true); }
    finally { setLoading(false); }
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(taxRate) || 0);
  const total = subtotal + tax;
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  const setItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addItem = () => setItems((a) => [...a, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await quoteService.createQuote({
        lead_id: leadId, service_type: serviceType,
        line_items: items.filter((i) => i.description.trim()).map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 })),
        tax_rate: Number(taxRate) || 0, valid_until: validUntil || null,
      });
      setShow(false); setItems([{ description: '', quantity: 1, unit_price: 0 }]); setTaxRate(0); setValidUntil('');
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not create quote.'); } finally { setSaving(false); }
  };

  if (disabled) {
    return <div className="border-t border-gray-200 pt-3 text-xs text-gray-500" data-testid="quotes-disabled">Quote Generation is not active (enable <code>quote_builder_enabled</code>).</div>;
  }

  return (
    <div className="border-t border-gray-200 pt-3 space-y-2" data-testid="quotes-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Quotes {loading ? '' : `(${quotes.length})`}</h3>
        <button type="button" onClick={() => setShow((s) => !s)} data-testid="quote-new-btn" className="text-sm text-blue-600 hover:underline">{show ? 'Cancel' : '+ New Quote'}</button>
      </div>

      {!loading && quotes.length > 0 && (
        <div className="space-y-1.5" data-testid="quotes-list">
          {quotes.map((q) => <QuoteRow key={q.id} q={q} onChanged={load} />)}
        </div>
      )}

      {show && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2" data-testid="quote-builder">
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <input value={it.description} onChange={(e) => setItem(idx, 'description', e.target.value)} placeholder="Description" data-testid={`quote-item-desc-${idx}`} className="col-span-6 py-1.5 px-2 text-sm border border-gray-300 rounded" />
              <input type="number" min="0" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} data-testid={`quote-item-qty-${idx}`} className="col-span-2 py-1.5 px-2 text-sm border border-gray-300 rounded" />
              <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, 'unit_price', e.target.value)} data-testid={`quote-item-price-${idx}`} className="col-span-3 py-1.5 px-2 text-sm border border-gray-300 rounded" />
              <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-500" aria-label="Remove line item"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={addItem} data-testid="quote-add-item" className="text-xs text-blue-600 hover:underline">+ Add line item</button>
          <div className="flex items-center gap-3 pt-1">
            <label className="text-xs text-gray-600">Tax rate <input type="number" min="0" max="1" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="quote-tax-rate" className="w-20 ml-1 py-1 px-2 text-sm border border-gray-300 rounded" /></label>
            <label className="text-xs text-gray-600">Valid until <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid-until" className="ml-1 py-1 px-2 text-sm border border-gray-300 rounded" /></label>
          </div>
          <div className="text-xs text-gray-700 flex gap-4" data-testid="quote-totals">
            <span>Subtotal <b>{money(subtotal)}</b></span><span>Tax <b>{money(tax)}</b></span><span>Total <b className="text-gray-900">{money(total)}</b></span>
          </div>
          {err && <p role="alert" className="text-xs text-red-600">{err}</p>}
          <button type="button" onClick={save} disabled={saving} data-testid="quote-save-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Draft Quote</button>
        </div>
      )}
    </div>
  );
};

export default LeadManager;